import { useCallback, useEffect, useRef, useState } from "react"
import styled, { css, keyframes } from "styled-components"
import { motion, useReducedMotion } from "framer-motion"
import emailjs from "emailjs-com"
import { useNavigate } from "react-router-dom"
import logo from "../assets/peplogo.png"
import playStoreIcon from "../assets/play-store.png"
import appStoreIcon from "../assets/app-store.png"
import heroScreen from "../assets/store/06-hero.jpg"
import momentsScreen from "../assets/store/01-moments.jpg"
import vibesScreen from "../assets/store/02-vibes.jpg"
import plansScreen from "../assets/store/03-plans.jpg"
import nearbyScreen from "../assets/store/04-nearby.jpg"
import pepScreen from "../assets/store/05-pep.jpg"

const storeLinks = {
  play: "https://play.google.com/store/apps/details?id=com.destiny.in",
  app: "https://apps.apple.com/in/app/destiny-nearby-network/id6739805983",
}

const features = [
  {
    id: "moments",
    badge: "moments",
    accent: "#5b7cff",
    glow: "#2a3cff",
    underline: "#ff5fa2",
    title: ["share your", "day."],
    highlight: null,
    copy: "Snap your day, friends add theirs, and any moment can start a vibe. Your everyday is already something to talk about.",
    chips: ["share your day", "friends add theirs", "start a vibe"],
    image: momentsScreen,
    alt: "Moments in the pepchat app: share your day, friends add theirs, start a vibe",
  },
  {
    id: "vibes",
    badge: "vibes",
    accent: "#ff6a3d",
    glow: "#ff7a1a",
    title: ["drop a", "topic."],
    highlight: "topic.",
    copy: "Throw out a question or something on your mind. People who relate drop their takes, so nobody has to open with an awkward \"hey\".",
    chips: ["12 takes", "closes in 4d", "no awkward hey"],
    image: vibesScreen,
    alt: "Vibes in the pepchat app: topics people can join with their takes",
  },
  {
    id: "plans",
    badge: "plans",
    accent: "#9b6bff",
    glow: "#7c3aed",
    underline: "#34d399",
    title: ["instant", "irl plans."],
    highlight: null,
    copy: "Coffee, cricket, a movie or a random drive. Start a meetup in seconds or tap \"I'm in\" on something nearby.",
    chips: ["instant meetup", "1 spot left", "i'm in →"],
    image: plansScreen,
    alt: "Plans in the pepchat app: coffee, dance night and movie meetups",
  },
  {
    id: "nearby",
    badge: "nearby",
    accent: "#34d399",
    glow: "#0f8f5f",
    title: ["plans around", "the corner."],
    highlight: "corner.",
    copy: "See what's happening around you on the map and tap in. Your next plan might be a few streets away.",
    chips: ["nearby", "today · 3 spots left", "tap in"],
    image: nearbyScreen,
    alt: "Nearby plans shown on a map in the pepchat app",
  },
  {
    id: "pep",
    badge: "pep ai",
    accent: "#e879f9",
    glow: "#a855f7",
    title: ["meet pep, your", "group's hype ghost."],
    highlight: "hype ghost.",
    copy: "Say @pep in any chat for polls, games and icebreakers, or to ask a question and search the web together without leaving the conversation.",
    chips: ["@pep", "made with pep", "polls · games · icebreakers"],
    image: pepScreen,
    alt: "Pep AI in a pepchat group chat creating a poll",
    gradient: true,
  },
]

const topics = [
  "anyone else learning guitar?",
  "what weird skill came in handy?",
  "chai or coffee, be honest",
  "who's up for cricket sunday?",
  "best underrated movie?",
  "what does being happy at 25 look like?",
  "badminton this weekend?",
  "how do you keep in touch with college friends?",
]

const conversations = [
  { label: "moment", color: "#5b7cff", first: "found my new evening chai spot ☕", reply: "where is this?? mine never looks that good" },
  { label: "vibe", color: "#ff6a3d", first: "anyone else learning guitar?", reply: "me, struggling with chords 😂" },
  { label: "plan", color: "#9b6bff", first: "badminton this sunday, need 2 more", reply: "i'm in. what time?" },
]

function StoreButtons({ centered = false }) {
  const openStore = (link) => window.open(link, "_blank", "noopener,noreferrer")

  return (
    <StoreButtonRow $centered={centered}>
      <StoreButton type="button" onClick={() => openStore(storeLinks.app)}>
        <StoreIcon src={appStoreIcon} alt="" />
        <StoreCopy><span>Download on the</span><strong>App Store</strong></StoreCopy>
      </StoreButton>
      <StoreButton type="button" onClick={() => openStore(storeLinks.play)}>
        <StoreIcon src={playStoreIcon} alt="" />
        <StoreCopy><span>Get it on</span><strong>Google Play</strong></StoreCopy>
      </StoreButton>
    </StoreButtonRow>
  )
}

function LandingPage() {
  const navigate = useNavigate()
  const reduceMotion = useReducedMotion()
  const [contactLoading, setContactLoading] = useState(false)
  const formRef = useRef(null)
  const productRef = useRef(null)
  const examplesRef = useRef(null)
  const downloadRef = useRef(null)
  const contactRef = useRef(null)

  const reveal = (delay = 0) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  })

  const scrollToRef = useCallback((ref) => ref.current?.scrollIntoView({
    behavior: reduceMotion ? "auto" : "smooth",
    block: "start",
  }), [reduceMotion])

  const handleDownloadClick = () => {
    const agent = navigator.userAgent || navigator.vendor || window.opera
    const link = /Mac|iPad|iPhone|iPod/.test(agent) ? storeLinks.app : storeLinks.play
    window.open(link, "_blank", "noopener,noreferrer")
  }

  const sendSupportMessage = (event) => {
    event.preventDefault()
    if (contactLoading) return
    setContactLoading(true)

    emailjs
      .sendForm("service_hdk9n79", "template_gyoj38v", formRef.current, "user_fvU0mTt1RZW7WSXTCN6ox")
      .then(() => {
        alert("Message sent successfully!")
        setContactLoading(false)
        formRef.current?.reset()
      })
      .catch((error) => {
        console.error(error)
        alert("Failed to send message. Please try again.")
        setContactLoading(false)
      })
  }

  useEffect(() => {
    const refs = {
      connect: productRef,
      what: productRef,
      why: examplesRef,
      examples: examplesRef,
      download: downloadRef,
      contact: contactRef,
      support: contactRef,
    }

    const handleHashChange = () => {
      const hash = window.location.hash.replace("#", "")
      if (hash === "privacy" || hash === "priacy") {
        navigate("/privacy", { replace: true })
        return
      }
      if (features.some((feature) => feature.id === hash)) {
        document.getElementById(hash)?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth" })
        return
      }
      if (hash && refs[hash]) scrollToRef(refs[hash])
    }

    handleHashChange()
    window.addEventListener("hashchange", handleHashChange)
    return () => window.removeEventListener("hashchange", handleHashChange)
  }, [navigate, reduceMotion, scrollToRef])

  return (
    <Page>
      <Header>
        <HeaderInner>
          <LogoLockup href="#top" aria-label="pepchat home">
            <LogoMark src={logo} alt="" />
            <LogoText>pepchat</LogoText>
          </LogoLockup>
          <Nav aria-label="Main navigation">
            <a href="#moments">moments</a>
            <a href="#vibes">vibes</a>
            <a href="#plans">plans</a>
            <a href="#pep">pep</a>
            <a href="#faq">faq</a>
          </Nav>
          <HeaderButton type="button" onClick={handleDownloadClick}>get the app</HeaderButton>
        </HeaderInner>
      </Header>

      <Main>
        <Hero id="top">
          <HeroGlow aria-hidden="true" />
          <HeroCopy {...reveal()}>
            <Badge $color="#ffffff">pepchat</Badge>
            <HeroTitle>give people a reason to <GradientText>vibe.</GradientText></HeroTitle>
            <HeroText>Sometimes you want someone to talk to. You just need something to start with.</HeroText>
            <FeatureLine>moments · vibes · plans · pep</FeatureLine>
            <StoreButtons centered />
            <HeroProof><LiveDot /> <strong>100K+ users</strong> on iOS & Android</HeroProof>
          </HeroCopy>
          <HeroShot {...reveal(0.15)}>
            <img src={heroScreen} alt="The Vibes feed in the pepchat app, between Moments and Plans" />
          </HeroShot>
        </Hero>

        <Ticker aria-label="Example Vibe topics">
          <TickerTrack>
            {[...topics, ...topics].map((topic, index) => (
              <span key={`${topic}-${index}`} aria-hidden={index >= topics.length}>{topic}</span>
            ))}
          </TickerTrack>
        </Ticker>

        <ProductSection ref={productRef} id="connect">
          <SectionIntro {...reveal()}>
            <Badge $color="#b8b8c2">how it works</Badge>
            <SectionTitle>every convo needs a <GradientText>starting point.</GradientText></SectionTitle>
            <SectionLead>Something to share, something to talk about, or something to do together. Pick yours.</SectionLead>
          </SectionIntro>

          {features.map((feature, index) => (
            <FeatureRow key={feature.id} id={feature.id} $reverse={index % 2 === 1} $glow={feature.glow}>
              <FeatureCopy {...reveal()}>
                <Badge $color={feature.accent}>{feature.badge}</Badge>
                <FeatureTitle $accent={feature.accent} $gradient={feature.gradient}>
                  {feature.title.map((line) => {
                    const highlight = feature.highlight && line.includes(feature.highlight)
                    if (!highlight) return <span key={line}>{line}</span>
                    const before = line.replace(feature.highlight, "")
                    return <span key={line}>{before}<em>{feature.highlight}</em></span>
                  })}
                </FeatureTitle>
                {feature.underline && <Squiggle $color={feature.underline} aria-hidden="true" />}
                <FeatureText>{feature.copy}</FeatureText>
                <Chips>
                  {feature.chips.map((chip, chipIndex) => (
                    <Chip key={chip} $accent={feature.accent} $index={chipIndex}>{chip}</Chip>
                  ))}
                </Chips>
              </FeatureCopy>
              <FeatureShot {...reveal(0.1)} $glow={feature.glow} $gradient={feature.gradient}>
                <img src={feature.image} alt={feature.alt} loading="lazy" />
              </FeatureShot>
            </FeatureRow>
          ))}
        </ProductSection>

        <ExamplesSection ref={examplesRef} id="examples">
          <SectionIntro {...reveal()}>
            <Badge $color="#ff5fa2">how it starts</Badge>
            <SectionTitle>one small opener. a real convo.</SectionTitle>
          </SectionIntro>
          <ConversationGrid>
            {conversations.map((conversation, index) => (
              <ConversationCard {...reveal(index * 0.08)} key={conversation.label} $color={conversation.color}>
                <ConversationLabel $color={conversation.color}>{conversation.label}</ConversationLabel>
                <Bubble>{conversation.first}</Bubble>
                <Bubble $reply $color={conversation.color}>{conversation.reply}</Bubble>
              </ConversationCard>
            ))}
          </ConversationGrid>
          <ExampleNote>Illustrative examples, not real user messages.</ExampleNote>
        </ExamplesSection>

        <FaqSection id="faq">
          <FaqInner>
            <SectionIntro {...reveal()}>
              <Badge $color="#b8b8c2">good to know</Badge>
              <SectionTitle>questions, answered.</SectionTitle>
            </SectionIntro>
            <FaqList>
              <details><summary>What is pepchat?</summary><p>pepchat is a mobile app that helps you find people to talk to and things to do, through everyday moments, shared topics and offline plans.</p></details>
              <details><summary>How are Moments, Vibes and Plans different?</summary><p>Moments share your everyday. Vibes gather people around a topic. Plans help people do something offline together. Each one is a different way to start.</p></details>
              <details><summary>What can @pep do?</summary><p>Say @pep in a chat for polls, games and icebreakers, or to ask a question, find information and search the web together without leaving the conversation.</p></details>
              <details><summary>How do I report abuse or get help?</summary><p>Use the <a href="/abuse">Report abuse</a> page for a concern about an account or content. For anything else, use the <a href="#contact">support form</a>.</p></details>
            </FaqList>
          </FaqInner>
        </FaqSection>

        <DownloadSection ref={downloadRef} id="download">
          <DownloadInner {...reveal()}>
            <SectionTitle>bring something.<br /><GradientText>see who joins.</GradientText></SectionTitle>
            <SectionLead>Share a moment, drop a topic or make a plan. Free on iOS and Android.</SectionLead>
            <StoreButtons centered />
          </DownloadInner>
        </DownloadSection>
      </Main>

      <Footer ref={contactRef} id="contact">
        <FooterInner>
          <FooterBrand>
            <LogoLockup href="#top"><LogoMark src={logo} alt="" /><LogoText>pepchat</LogoText></LogoLockup>
            <p>give people a reason to vibe.</p>
          </FooterBrand>

          <SupportForm ref={formRef} onSubmit={sendSupportMessage}>
            <SupportTitle>contact support</SupportTitle>
            <FieldGrid>
              <label>Name<input name="from_name" type="text" autoComplete="name" required /></label>
              <label>Email<input name="reply_to" type="email" autoComplete="email" required /></label>
            </FieldGrid>
            <label>Phone <small>optional</small><input name="phone" type="tel" autoComplete="tel" /></label>
            <label>Message<textarea name="message" placeholder="How can we help?" required /></label>
            <SubmitButton type="submit" disabled={contactLoading}>{contactLoading ? "Sending..." : "Send message"}</SubmitButton>
          </SupportForm>

          <FooterBottom>
            <SocialLinks><a href="https://www.instagram.com/pepchat.in/" target="_blank" rel="noreferrer">Instagram</a><a href="https://www.linkedin.com/company/96934048/" target="_blank" rel="noreferrer">LinkedIn</a></SocialLinks>
            <PolicyLinks><a href="/privacy">Privacy</a><a href="/abuse">Report abuse</a><span>© 2026 pepchat</span></PolicyLinks>
          </FooterBottom>
        </FooterInner>
      </Footer>
    </Page>
  )
}

export default LandingPage

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: .45; transform: scale(.82); }
`

const scroll = keyframes`
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(-50%, 0, 0); }
`

const gradient = css`
  background: linear-gradient(90deg, #a78bfa 0%, #e879f9 45%, #ff7a45 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`

const display = css`
  font-family: "Inter Tight", "Inter", "Manrope", sans-serif;
  font-weight: 800;
  letter-spacing: -0.045em;
  text-transform: lowercase;
`

const Page = styled.div`
  --bg: #0a0a0c;
  --surface: #141417;
  --surface-2: #1c1c20;
  --line: #2a2a30;
  --muted: #a1a1aa;
  width: 100%;
  max-width: 100vw;
  min-width: 0;
  align-self: stretch;
  min-height: 100vh;
  overflow-x: hidden;
  background: var(--bg);
  color: #f6f6f7;
  font-family: "Manrope", "Segoe UI", sans-serif;

  *, *::before, *::after { box-sizing: border-box; }
  button, input, textarea { font: inherit; }
  a, button { -webkit-tap-highlight-color: transparent; }
  :focus-visible { outline: 2px solid #e879f9; outline-offset: 4px; }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after { animation: none !important; scroll-behavior: auto !important; transition: none !important; }
  }
`

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 20;
  width: 100%;
  border-bottom: 1px solid rgba(255,255,255,.06);
  background: rgba(10,10,12,.78);
  backdrop-filter: blur(18px);
`

const HeaderInner = styled.div`
  width: min(1180px, calc(100% - 48px));
  min-height: 72px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  @media (max-width: 680px) { width: calc(100% - 32px); min-height: 64px; gap: 12px; }
`

const LogoLockup = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  text-decoration: none;
`

const LogoMark = styled.img`width: 36px; height: 36px; border-radius: 10px; flex: 0 0 auto;`
const LogoText = styled.strong`${display} font-size: 1.4rem;`

const Nav = styled.nav`
  display: flex;
  gap: 6px;
  padding: 5px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: rgba(255,255,255,.03);
  a { color: #c4c4cc; text-decoration: none; font-size: .88rem; font-weight: 700; padding: 8px 14px; border-radius: 999px; }
  a:hover { color: #fff; background: rgba(255,255,255,.08); }
  @media (max-width: 820px) { display: none; }
`

const HeaderButton = styled.button`
  min-height: 44px;
  border: 0;
  border-radius: 999px;
  padding: 0 20px;
  background: #fff;
  color: #0a0a0c;
  font-size: .9rem;
  font-weight: 800;
  cursor: pointer;
  transition: transform .2s ease;
  &:hover { transform: translateY(-2px) rotate(-1deg); }
`

const Main = styled.main`width: 100%;`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 9px 18px;
  border: 1px solid ${({ $color }) => `${$color}55`};
  border-radius: 999px;
  background: ${({ $color }) => `${$color}14`};
  color: #fff;
  font-size: .78rem;
  font-weight: 800;
  letter-spacing: .22em;
  text-transform: uppercase;
  &::before { content: ""; width: 8px; height: 8px; border-radius: 50%; background: ${({ $color }) => $color}; }
`

const GradientText = styled.span`${gradient}`

const Hero = styled.section`
  position: relative;
  padding: 72px 24px 0;
  text-align: center;
  overflow: hidden;
  @media (max-width: 600px) { padding-top: 48px; }
`

const HeroGlow = styled.div`
  position: absolute;
  inset: -20% -10% auto;
  height: 900px;
  background:
    radial-gradient(40% 40% at 30% 30%, rgba(124,58,237,.28), transparent 70%),
    radial-gradient(35% 35% at 75% 40%, rgba(255,106,61,.18), transparent 70%);
  pointer-events: none;
`

const HeroCopy = styled(motion.div)`
  position: relative;
  max-width: 880px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;
`

const HeroTitle = styled.h1`
  ${display}
  margin: 6px 0 0;
  font-size: clamp(3rem, 9vw, 6.6rem);
  line-height: .92;
  text-wrap: balance;
`

const HeroText = styled.p`
  margin: 0;
  max-width: 560px;
  color: #d4d4d8;
  font-size: clamp(1.05rem, 2vw, 1.25rem);
  line-height: 1.55;
`

const FeatureLine = styled.p`margin: 0; color: var(--muted); font-size: 1rem; font-weight: 600;`

const StoreButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: ${({ $centered }) => ($centered ? "center" : "flex-start")};
  margin-top: 6px;
`

const StoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 11px;
  min-height: 58px;
  padding: 9px 20px 9px 16px;
  border: 1px solid #34343a;
  border-radius: 16px;
  background: #fff;
  color: #0a0a0c;
  text-align: left;
  cursor: pointer;
  transition: transform .2s ease, box-shadow .2s ease;
  &:hover { transform: translateY(-3px); box-shadow: 0 12px 30px rgba(232,121,249,.25); }
`

const StoreIcon = styled.img`width: 26px; height: 26px; object-fit: contain;`
const StoreCopy = styled.span`
  span, strong { display: block; }
  span { font-size: .68rem; color: #52525b; }
  strong { font-size: 1.02rem; margin-top: 1px; }
`

const HeroProof = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--muted);
  font-size: .9rem;
  strong { color: #fff; }
`

const LiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 12px #34d399;
  animation: ${pulse} 2s ease-in-out infinite;
`

const HeroShot = styled(motion.div)`
  position: relative;
  width: min(100%, 640px);
  margin: 56px auto 0;
  img { display: block; width: 100%; height: auto; }
  &::after {
    content: "";
    position: absolute;
    inset: auto 0 0;
    height: 40%;
    background: linear-gradient(transparent, var(--bg));
  }
`

const Ticker = styled.div`
  max-width: 100vw;
  overflow: hidden;
  border-block: 1px solid var(--line);
  background: #0f0f12;
  padding: 18px 0;
  transform: rotate(-1.2deg);
  margin: -24px -10px 0;
`

const TickerTrack = styled.div`
  display: flex;
  width: max-content;
  gap: 14px;
  animation: ${scroll} 40s linear infinite;
  span {
    flex: 0 0 auto;
    padding: 10px 18px;
    border-radius: 999px;
    background: var(--surface-2);
    border: 1px solid var(--line);
    color: #e4e4e7;
    font-weight: 700;
    font-size: .95rem;
    white-space: nowrap;
  }
  span:nth-child(3n + 1) { background: #ff6a3d; border-color: #ff6a3d; color: #160a05; }
  span:nth-child(5n + 2) { background: #fff; color: #0a0a0c; }
`

const ProductSection = styled.section`
  padding: 120px 24px 40px;
  scroll-margin-top: 80px;
  @media (max-width: 700px) { padding-top: 88px; }
`

const SectionIntro = styled(motion.div)`
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const SectionTitle = styled.h2`
  ${display}
  margin: 22px 0 16px;
  font-size: clamp(2.4rem, 6vw, 4.4rem);
  line-height: .98;
  text-wrap: balance;
`

const SectionLead = styled.p`
  margin: 0;
  max-width: 560px;
  color: var(--muted);
  font-size: 1.08rem;
  line-height: 1.65;
`

const FeatureRow = styled.article`
  position: relative;
  width: min(1120px, 100%);
  margin: 110px auto 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: clamp(32px, 6vw, 88px);
  scroll-margin-top: 90px;

  > :first-child { order: ${({ $reverse }) => ($reverse ? 2 : 1)}; }
  > :last-child { order: ${({ $reverse }) => ($reverse ? 1 : 2)}; }

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    margin-top: 88px;
    > :first-child { order: 1; }
    > :last-child { order: 2; }
  }
`

const FeatureCopy = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  @media (max-width: 860px) { align-items: center; text-align: center; }
`

const FeatureTitle = styled.h3`
  ${display}
  margin: 24px 0 0;
  font-size: clamp(2.8rem, 6.4vw, 5rem);
  line-height: .95;
  span { display: block; }
  em {
    font-style: normal;
    ${({ $gradient, $accent }) => ($gradient ? css`color: #2a0a3d; -webkit-text-stroke: 0;` : css`color: ${$accent};`)}
  }
  ${({ $gradient }) => $gradient && css`em { ${gradient} }`}
`

const Squiggle = styled.span`
  display: block;
  width: 180px;
  height: 16px;
  margin-top: 10px;
  background: ${({ $color }) => `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 180 16'%3E%3Cpath d='M3 10c20-8 30 6 50-2s30-8 50 0 30 6 50-2 20-4 24-2' fill='none' stroke='${encodeURIComponent($color)}' stroke-width='5' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat`};
`

const FeatureText = styled.p`
  margin: 24px 0 0;
  max-width: 460px;
  color: #d4d4d8;
  font-size: 1.12rem;
  line-height: 1.65;
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 28px;
  @media (max-width: 860px) { justify-content: center; }
`

const Chip = styled.span`
  ${display}
  letter-spacing: -0.02em;
  padding: 10px 18px;
  border-radius: 999px;
  font-size: 1.02rem;
  background: ${({ $index, $accent }) => ($index === 0 ? $accent : $index === 1 ? "#fff" : "transparent")};
  color: ${({ $index }) => ($index === 2 ? "#fff" : "#0a0a0c")};
  border: 2px solid ${({ $index, $accent }) => ($index === 2 ? $accent : "transparent")};
  transform: rotate(${({ $index }) => [-3, 2, -1][$index] || 0}deg);
`

const FeatureShot = styled(motion.div)`
  position: relative;
  width: min(100%, 460px);
  justify-self: center;
  border-radius: 36px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,.08);
  box-shadow: 0 30px 120px ${({ $glow }) => `${$glow}55`};
  img { display: block; width: 100%; height: auto; }
`

const ExamplesSection = styled.section`
  padding: 140px 24px 40px;
  scroll-margin-top: 80px;
`

const ConversationGrid = styled.div`
  width: min(1080px, 100%);
  margin: 48px auto 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  @media (max-width: 860px) { grid-template-columns: 1fr; max-width: 460px; }
`

const ConversationCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 24px;
  border-radius: 28px;
  border: 1px solid var(--line);
  background: linear-gradient(160deg, ${({ $color }) => `${$color}22`}, var(--surface) 55%);
`

const ConversationLabel = styled.span`
  align-self: flex-start;
  margin-bottom: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  color: #0a0a0c;
  font-size: .72rem;
  font-weight: 800;
  letter-spacing: .16em;
  text-transform: uppercase;
`

const Bubble = styled.p`
  margin: 0;
  max-width: 88%;
  padding: 12px 16px;
  border-radius: 20px;
  font-size: .98rem;
  font-weight: 600;
  line-height: 1.45;
  ${({ $reply, $color }) => ($reply
    ? css`align-self: flex-end; background: ${$color}; color: #0a0a0c; border-bottom-right-radius: 6px;`
    : css`align-self: flex-start; background: var(--surface-2); color: #f4f4f5; border-bottom-left-radius: 6px;`)}
`

const ExampleNote = styled.p`margin: 18px 0 0; text-align: center; color: #71717a; font-size: .8rem;`

const FaqSection = styled.section`padding: 130px 24px 40px; scroll-margin-top: 80px;`

const FaqInner = styled.div`width: min(760px, 100%); margin: 0 auto;`

const FaqList = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 40px;

  details {
    border: 1px solid var(--line);
    border-radius: 22px;
    background: var(--surface);
    padding: 0 24px;
  }
  details[open] { background: var(--surface-2); }
  summary {
    list-style: none;
    cursor: pointer;
    padding: 22px 0;
    font-weight: 800;
    font-size: 1.05rem;
    display: flex;
    justify-content: space-between;
    gap: 16px;
  }
  summary::-webkit-details-marker { display: none; }
  summary::after { content: "+"; font-size: 1.4rem; line-height: 1; color: var(--muted); }
  details[open] summary::after { content: "–"; }
  p { margin: 0 0 22px; color: #c4c4cc; line-height: 1.7; }
  a { color: #e879f9; }
`

const DownloadSection = styled.section`padding: 130px 24px; scroll-margin-top: 80px;`

const DownloadInner = styled(motion.div)`
  width: min(1080px, 100%);
  margin: 0 auto;
  padding: clamp(48px, 8vw, 96px) 24px;
  border-radius: 40px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background:
    radial-gradient(60% 80% at 20% 0%, rgba(124,58,237,.45), transparent 70%),
    radial-gradient(60% 80% at 90% 100%, rgba(255,106,61,.35), transparent 70%),
    var(--surface);
  border: 1px solid var(--line);
  ${SectionTitle} { margin-top: 0; }
  ${StoreButtonRow} { margin-top: 24px; }
`

const Footer = styled.footer`border-top: 1px solid var(--line); background: #070709; scroll-margin-top: 80px;`

const FooterInner = styled.div`
  width: min(1120px, calc(100% - 48px));
  margin: 0 auto;
  padding: 64px 0 28px;
  display: grid;
  grid-template-columns: 1fr minmax(0, 520px);
  gap: 48px;
  @media (max-width: 860px) { grid-template-columns: 1fr; width: calc(100% - 32px); }
`

const FooterBrand = styled.div`
  p { margin: 14px 0 0; color: var(--muted); font-size: 1.05rem; font-weight: 700; }
`

const SupportForm = styled.form`
  display: grid;
  gap: 14px;
  label { display: grid; gap: 7px; color: #d4d4d8; font-size: .86rem; font-weight: 700; }
  small { color: #71717a; font-weight: 600; }
  input, textarea {
    width: 100%;
    min-width: 0;
    border: 1px solid var(--line);
    border-radius: 14px;
    background: var(--surface);
    color: #fff;
    padding: 13px 14px;
    font-size: 16px;
  }
  input:focus, textarea:focus { border-color: #e879f9; outline: none; }
  textarea { min-height: 110px; resize: vertical; }
`

const SupportTitle = styled.h2`${display} margin: 0 0 4px; font-size: 1.8rem;`

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`

const SubmitButton = styled(HeaderButton)`
  justify-self: start;
  min-height: 48px;
  padding: 0 26px;
  &:disabled { opacity: .6; cursor: wait; transform: none; }
`

const FooterBottom = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 18px;
  padding-top: 24px;
  border-top: 1px solid var(--line);
  color: #71717a;
  font-size: .86rem;
  a { color: #c4c4cc; text-decoration: none; }
  a:hover { color: #fff; text-decoration: underline; }
`

const SocialLinks = styled.div`display: flex; gap: 20px;`
const PolicyLinks = styled.div`display: flex; flex-wrap: wrap; gap: 20px;`
